// Global currency store — shared across all consumers (header switcher, /pricing
// select, PriceBadge). Lifted out of useCurrency so all components see the same
// state in the same tab without depending on the storage event.
import { create } from "zustand";

export type CurrencyCode =
  | "EUR" | "USD" | "NGN" | "XOF" | "XAF" | "GNF" | "KES" | "GHS"
  | "BRL" | "MAD" | "MXN" | "ARS"
  | "EGP" | "ZAR" | "COP" | "IDR" | "PHP" | "VND" | "INR" | "GBP";

export const CURRENCIES: Record<CurrencyCode, { symbol: string; label: string }> = {
  EUR: { symbol: "€", label: "Euro" },
  USD: { symbol: "$", label: "US Dollar" },
  NGN: { symbol: "₦", label: "Nigerian Naira" },
  XOF: { symbol: "CFA ", label: "West African CFA Franc" },
  XAF: { symbol: "FCFA ", label: "Central African CFA Franc" },
  GNF: { symbol: "GNF ", label: "Guinean Franc" },
  KES: { symbol: "KSh ", label: "Kenyan Shilling" },
  GHS: { symbol: "GH₵ ", label: "Ghanaian Cedi" },
  BRL: { symbol: "R$", label: "Brazilian Real" },
  MAD: { symbol: "MAD ", label: "Moroccan Dirham" },
  MXN: { symbol: "MX$", label: "Mexican Peso" },
  ARS: { symbol: "ARS ", label: "Argentine Peso" },
  EGP: { symbol: "E£", label: "Egyptian Pound" },
  ZAR: { symbol: "R", label: "South African Rand" },
  COP: { symbol: "COL$", label: "Colombian Peso" },
  IDR: { symbol: "Rp", label: "Indonesian Rupiah" },
  PHP: { symbol: "₱", label: "Philippine Peso" },
  VND: { symbol: "₫", label: "Vietnamese Dong" },
  INR: { symbol: "₹", label: "Indian Rupee" },
  GBP: { symbol: "£", label: "British Pound" },
};

/** Clean rounding anchors per currency (in major units). */
export const ROUNDING_ANCHORS: Partial<Record<CurrencyCode, number>> = {
  NGN: 500, XOF: 500, XAF: 500, GNF: 5000, KES: 50, GHS: 5,
  BRL: 10, MAD: 10, MXN: 10, ARS: 500, EGP: 10, ZAR: 10,
  COP: 5000, IDR: 5000, PHP: 50, VND: 10000, INR: 50,
  USD: 1, EUR: 1, GBP: 1,
};

export function roundToAnchor(major: number, currency: CurrencyCode): number {
  const a = ROUNDING_ANCHORS[currency] ?? 1;
  if (a <= 0) return Math.round(major);
  return Math.round(major / a) * a;
}

const STORAGE_KEY = "bp.currency";
const MANUAL_FLAG_KEY = "bp.currency.manual";

function readStored(): CurrencyCode | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && v in CURRENCIES ? (v as CurrencyCode) : null;
  } catch {
    return null;
  }
}

interface CurrencyState {
  currency: CurrencyCode;
  manuallySet: boolean;
  setCurrency: (c: CurrencyCode, opts?: { manual?: boolean }) => void;
  clearManual: () => void;
}

function readManualFlag(): boolean {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem(MANUAL_FLAG_KEY) === "1"; } catch { return false; }
}

export const useCurrencyStore = create<CurrencyState>((set) => ({
  currency: readStored() ?? "EUR",
  manuallySet: readManualFlag(),
  setCurrency: (c, opts) => {
    if (typeof window !== "undefined") {
      try { window.localStorage.setItem(STORAGE_KEY, c); } catch { /* noop */ }
      if (opts?.manual) {
        try { window.localStorage.setItem(MANUAL_FLAG_KEY, "1"); } catch { /* noop */ }
      }
    }
    set((s) => ({ currency: c, manuallySet: opts?.manual ? true : s.manuallySet }));
  },
  clearManual: () => {
    if (typeof window !== "undefined") {
      try { window.localStorage.removeItem(MANUAL_FLAG_KEY); } catch { /* noop */ }
    }
    set({ manuallySet: false });
  },
}));

// Cross-tab sync
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY && e.newValue && e.newValue in CURRENCIES) {
      useCurrencyStore.setState({ currency: e.newValue as CurrencyCode });
    }
  });
}

/**
 * Locale-aware price formatter.
 * - EN/USD-style: symbol BEFORE amount ("$199", "€199")
 * - FR/ES/PT for CFA/FCFA/KSh: amount BEFORE symbol with thin space thousands ("130 000 FCFA")
 */
export function formatMinor(amountMinor: number, currency: CurrencyCode, locale: string = "en"): string {
  const noDecimals = ["NGN", "XOF", "XAF", "GNF", "KES", "ARS"].includes(currency);
  const major = noDecimals ? Math.round(amountMinor / 100) : amountMinor / 100;
  const lang = (locale || "en").slice(0, 2).toLowerCase();
  const intlLocale = lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : lang === "pt" ? "pt-BR" : "en-US";
  const num = major.toLocaleString(intlLocale, {
    minimumFractionDigits: noDecimals ? 0 : 2,
    maximumFractionDigits: noDecimals ? 0 : 2,
  });
  const sym = CURRENCIES[currency]?.symbol ?? `${currency} `;
  const symbolAfter = (lang !== "en" && ["XOF", "XAF", "GNF", "KES", "GHS", "EUR", "MAD", "ARS"].includes(currency))
    || ["MAD", "ARS"].includes(currency);
  if (symbolAfter) {
    // Use ISO-like suffix; for XAF use FCFA, for XOF use CFA, KES → KSh, GHS → GH₵
    const suffix = sym.trim();
    return `${num} ${suffix}`;
  }
  return `${sym}${num}`;
}